import '@gig/pp-checkout';
import type { CheckoutTracker } from '@gig/checkout-shared-tracker';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import * as Sentry from '@sentry/browser';

import './header.js';
import './footer.js';
import './pp-policy.js';
import './extra-info.js';
import './pp-feedback.js';
import './pp-skeleton.js';
import './certification.js';
import './loading-spinner.js';
import { globalStyles, skeletonStyles } from '../global-styles.js';
import { resolveCheckoutLocale, setLocale } from '../locale/localization.js';
import { IOrderInfo } from '../types/order.js';
import { IMerchantCustomizeConfig } from '../types/customSettings.js';
import { CHECKOUT_FAILED_EVENT } from '../constant.js';
import { createFrontCashierTracker, syncFrontCashierOrder } from '../tracker/cashier.js';
import {
  clearCheckoutSentryContext,
  shouldSendCheckoutSentryEvent,
  syncCheckoutSentryContext,
} from '../utils/sentry.js';

@customElement('pp-cashier')
export class PpCashier extends LitElement {
  static styles = [
    globalStyles,
    skeletonStyles,
    css`
      :host {
        display: block;
        color: var(--pp-primary-text-color);
        font-family: var(--pp-checkout-font-family);
        background-color: var(--pp-body-background, rgb(250, 250, 250));
      }

      .pp-checkout-container {
        margin: 0 auto;
        max-width: 36rem;
        padding-top: 24px;
      }

      .pp-checkout-container {
        display: flex;
        min-height: calc(100vh - 48px);
        padding-top: 1.5rem;
        padding-bottom: 1rem;
        flex-direction: column;
      }

      pp-checkout {
        margin: -12px;
      }

      .pp-checkout-core {
        min-height: 479px;
        overflow: hidden;
      }

      pp-discount {
        display: block;
      }

      @media only screen and (min-width: 0) and (max-width: 576px) {
        .pp-checkout-container {
          padding-top: 0;
          padding-bottom: 0;
        }

        pp-checkout {
          margin: 0;
          flex-grow: 1;
          border-radius: 0;
          min-height: unset;
        }

        .skeleton {
          padding: 0 12px;
        }

        pp-discount {
          display: none;
        }

        pp-certification {
          margin-top: auto;
        }
      }
    `,
  ];

  @state() private locale: string = '';

  @state() private _orderInfo!: null | IOrderInfo;

  @state() private _cancelUrl: string = '';

  @state() private _customizeConfig: Partial<IMerchantCustomizeConfig> = {};

  @state() private _fulfilled: boolean = false;

  private _tracker: CheckoutTracker = createFrontCashierTracker();

  @state() private checkoutFailedCount: number = 0;

  @state() private timeId?: number;

  @state() private pageStayTime: number = 0;

  @state() private discountDescription: string | undefined;

  @state() private visibilityChangeHandler?: () => void;

  private static sentryInitialized = false;

  /**
   * 初始化Sentry
   */
  initSentry() {
    try {
      const { VITE_ENV, VITE_SENTRY_DSN } = import.meta.env;

      if (VITE_ENV !== 'DEV' && !PpCashier.sentryInitialized) {
        Sentry.init({
          dsn: VITE_SENTRY_DSN,
          environment: VITE_ENV,
          beforeSend(event) {
            return shouldSendCheckoutSentryEvent(event) ? event : null;
          },
          integrations: [
            // 错误追踪，开启
            Sentry.browserTracingIntegration(),
          ],
          tracesSampleRate: 1.0,
          tracePropagationTargets: ['localhost', /^https:\/\/pingpongx.com/],
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
        PpCashier.sentryInitialized = true;
      }
    } catch (error) {
      console.error(error);
    }
  }

  // 生命周期方法 ================================================================

  connectedCallback(): void {
    this.initSentry();
    if (PpCashier.sentryInitialized) {
      clearCheckoutSentryContext(Sentry);
    }
    super.connectedCallback();
    window.addEventListener(CHECKOUT_FAILED_EVENT, this.handleCheckoutFailed.bind(this));
    this.startVisibilityAwareTimer();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(CHECKOUT_FAILED_EVENT, this.handleCheckoutFailed.bind(this));
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler!);
  }

  // Getter方法 ================================================================

  /**
   * 根据商户配置动态设置额外信息模块样式
   */
  get extraStyle() {
    const customizeStyles = this._orderInfo?.storeCustomizeInfo?.customizeStyles;
    const map = {
      left: 'justify-between',
      center: 'justify-center',
      right: 'justify-between flex-direction',
    };
    return customizeStyles?.returnPosition ? map[customizeStyles.returnPosition] : map.left;
  }

  // 事件处理 ===================================================================

  /**
   *
   * 处理 `pp-checkout` 抛出的 `dispatch-order` 事件。
   *
   * 当前事件在类型层可能同时出现成功对象和失败字符串，
   * front 只在成功拉单并拿到订单实体时更新商户定制信息、页面展示态和 tracker 订单上下文。
   *
   * @param customEvent `pp-checkout` 抛出的订单事件
   */
  private _handleDispatch(customEvent: CustomEvent<IOrderInfo | string>) {
    const { detail } = customEvent;

    if (!detail || typeof detail !== 'object') {
      return;
    }

    this._customizeConfig = window.PingPong?.Checkout?.customizeConfig ?? {};
    this._renderShortcutIcon();
    if (PpCashier.sentryInitialized) {
      clearCheckoutSentryContext(Sentry);
      syncCheckoutSentryContext(Sentry, detail, window.__CHECKOUT__UUID__);
    }
    this._orderInfo = detail;
    syncFrontCashierOrder(this._tracker, detail);
  }

  /**
   * 支付核心包支付失败后执行
   */
  private handleCheckoutFailed() {
    this.checkoutFailedCount += 1;
  }

  /**
   * 基于页面可见性的计时逻辑
   */
  private startVisibilityAwareTimer() {
    // 页面隐藏时暂停计时
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        this.clearTimer();
      } else {
        this.resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    this.resumeTimer();
  }

  /**
   * 单次定时器, >= 10s时停止计时
   */
  private resumeTimer() {
    if (this.pageStayTime >= 10) {
      return this.clearTimer();
    }

    const startTime = Date.now();
    const updateTime = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      this.pageStayTime = Math.min(elapsed, 10); // 最大记录10秒

      if (this.pageStayTime < 10 && !document.hidden) {
        this.timeId = window.setTimeout(updateTime, 1000);
      }
    };

    this.timeId = window.setTimeout(updateTime, 1000);
  }

  /**
   * 清除定时器
   */
  private clearTimer() {
    if (this.timeId) {
      clearTimeout(this.timeId);
      this.timeId = undefined;
    }
  }

  /**
   * 是否显示反馈模块:
   * (连续支付失败2次 || 收银台页面停留超过10s) && 该 accId 下配置开启
   */
  getIsShowFeedback() {
    const { orderId, displayCashierTool } = this._orderInfo || {};
    return (
      !!orderId &&
      !!displayCashierTool &&
      (this.checkoutFailedCount >= 2 || this.pageStayTime >= 10)
    );
  }

  /**
   *
   * 处理壳层语言切换，并同步页面方向。
   *
   * front 一期不再沿用旧的手动语言切换埋点；
   * 这里只维护实际展示语言，供后续 front 自己的异常诊断事件复用。
   *
   * @param customEvent 语言切换事件
   */
  private _localeChange(customEvent: CustomEvent<string>) {
    const { detail } = customEvent;
    const locale = resolveCheckoutLocale(detail);
    void this._setLocale(locale);
    document.body.style.direction = locale === 'ar' ? 'rtl' : 'ltr';
  }

  /**
   *
   * 设置当前壳层展示语言，并把最终语言同步写回 tracker。
   *
   * 这样即便订单语言缺失、回退到了浏览器语言，
   * front 结果页后续的异常诊断事件也能带上当前真实展示语言。
   *
   * @param locale 当前需要设置的语言
   */
  private async _setLocale(locale: string) {
    const resolvedLocale = resolveCheckoutLocale(locale);
    this.locale = resolvedLocale;
    this._tracker.updateOrderContext({
      displayLanguage: resolvedLocale,
    });
    await setLocale(resolvedLocale);
  }

  /**
   * 设置收银台语言
   * 1. 根据订单传入的 language 设置当前语言
   * 2. 没取到时, 获取浏览器默认语言
   */
  private _setCashierLanguage() {
    if (!this._orderInfo) return;
    const { language } = this._orderInfo;
    void this._setLocale(language || navigator.language);
    this._fulfilled = true;
  }

  /**
   * 渲染商户定制网页快捷图标
   * @param iconPath - 图标文件的路径
   */
  private _renderShortcutIcon(): void {
    const head: HTMLHeadElement | null = document.head;

    const { browserTabIconUrl, tabTitle } = this._customizeConfig;

    if (browserTabIconUrl) {
      const link: HTMLLinkElement = document.createElement('link');
      link.rel = 'favicon icon';
      link.href = browserTabIconUrl;
      head.appendChild(link);
    }

    if (tabTitle) {
      document.title = tabTitle;
    }
  }

  private _getCancelUrl() {
    let { shopperCancelUrl, shopperResultUrl } = this._orderInfo ?? {};
    let _url = shopperCancelUrl || shopperResultUrl || '';
    if (!_url) return;
    const separator = _url.includes('?') ? '&' : '?';
    this._cancelUrl = `${_url}${separator}type=cancel`;
  }

  private _setDiscountDescription() {
    let { cashierActivityBanner } = this._orderInfo ?? {};

    this.discountDescription = cashierActivityBanner;
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('_orderInfo')) {
      this._getCancelUrl();
      this._setCashierLanguage();
      this._setDiscountDescription();
    }
  }

  private _renderFooter() {
    const { returnBtnText, displayPCI = false, displayReturnBtn = false } = this._customizeConfig;

    const displayReturn = !!this._cancelUrl && displayReturnBtn;

    const returnBtn = displayReturn
      ? html`
          <pp-cashier-extra
            cancelUrl=${this._cancelUrl}
            returnBtnText=${ifDefined(returnBtnText)}
          ></pp-cashier-extra>
        `
      : '';

    const feedback = this.getIsShowFeedback()
      ? html` <pp-feedback orderId=${this._orderInfo!.orderId}></pp-feedback> `
      : '';

    const pci = displayPCI
      ? html` <pp-certification .displayReturn=${displayReturn}></pp-certification> `
      : '';

    const footer = html`
      <pp-cashier-footer
        locale=${this.locale}
        cancelUrl=${this._cancelUrl}
        .customizeConfig=${this._customizeConfig}
        @locale-changed=${this._localeChange}
      >
      </pp-cashier-footer>
    `;

    const policy = html`<pp-policy></pp-policy>`;

    return html`
      <div class="flex items-center gap-2 ${this.extraStyle}">${returnBtn} ${feedback}</div>
      ${pci} ${footer} ${policy}
    `;
  }

  private _renderDiscountBanner() {
    return this.discountDescription
      ? html`<pp-discount description=${this.discountDescription}></pp-discount>`
      : '';
  }

  render() {
    return html`
      <div class="pp-cashier">
        ${this._renderDiscountBanner()}
        <pp-cashier-header
          locale=${this.locale}
          .fulfilled=${this._fulfilled}
          .customizeConfig=${this._customizeConfig}
          @locale-changed=${this._localeChange}
        ></pp-cashier-header>
        <div class="pp-checkout-container">
          <div class="pp-checkout-core">
            ${!this._fulfilled ? html`<pp-skeleton></pp-skeleton>` : ''}
            <pp-checkout
              locale="${this.locale}"
              accessToken=${window.__TOKEN__}
              .trackerContext=${this._tracker.exportBridgeContext()}
              @dispatch-order="${this._handleDispatch}"
              @pp-checkout-failed=${this.handleCheckoutFailed}
              class=${this._fulfilled ? '' : 'hidden'}
            ></pp-checkout>
          </div>

          <div class="m-t-auto">
            ${!this._fulfilled
              ? html`
                  <div class="skeleton">
                    <div class="LOADING-payment-header LOADING-loads"></div>
                    <div class="LOADING-payment-row LOADING-loads"></div>
                  </div>
                `
              : this._renderFooter()}
          </div>
        </div>
      </div>
    `;
  }
}
